"use client";

/**
 * Phase 19 / Plan 19-04 (LAND-01/06) — Site Control first-party lead form.
 *
 * This is the conversion endpoint of the whole GTM motion. It is the client
 * surface for the public, unauthenticated POST /api/site-control/lead route
 * (Plan 19-03). It mirrors that route's Zod schema client-side for UX, but the
 * server is the source of truth — client validation is purely to surface
 * errors before the round-trip.
 *
 * Design contract (do NOT change without the plan):
 *   - REQUIRED: work email, company, full name, # active sites, role.
 *   - OPTIONAL: current tool, timeline.
 *   - NO contact-number field is collected (deliberate — cold marketing
 *     traffic; matches the server route's design contract).
 *   - Honeypot "website" field (visually hidden, aria-hidden, tabindex -1):
 *     bots that fill every input trip it; the server returns a benign 200.
 *   - UNTICKED GDPR consent checkbox + a /legal/privacy link; the submit
 *     button stays disabled until consent is ticked (LAND-06).
 *   - <TurnstileWidget/> (Plan 19-02). Cloudflare injects a hidden
 *     `cf-turnstile-response` input inside the form; we read it at submit time
 *     (via `readTurnstileToken`, which also handles the no-site-key manual
 *     input) and post it as `turnstileToken`. The server does NOT simply fall
 *     open off production — `verifyTurnstileToken` rejects an EMPTY token
 *     before it reads the secret, and only falls back to a NODE_ENV check when
 *     TURNSTILE_SECRET_KEY is unset, which it is not locally.
 *
 * Submit handling mirrors setup-service-intake-form.tsx (the richest form-UX
 * reference): field-level error mapping, sonner toast fallbacks, in-flight
 * disabled state. The route returns the same 202 on accepted create/dedupe,
 * 400 on validation, 429 when rate-limited.
 *
 * All colours are design tokens (brand orange via `bg-accent`/`text-brand-700`
 * token classes), never hardcoded hex — the hex guardrail covers components/.
 */

import { useRef, useState } from "react";
import Link from "next/link";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  TurnstileWidget,
  readTurnstileToken,
  resetTurnstile,
  resolveTurnstileSiteKey,
} from "@/components/ui/turnstile-widget";
import {
  readAdUserDataConsent,
  readBrowserAttribution,
  readBrowserAttributionWithCurrentUrl,
} from "@/components/analytics/browser-attribution";
import { getOrCreatePublicFormSubmissionId } from "@/lib/public-form-submission-id";
import { SITE_CONTROL_NAME, SITE_CONTROL_CTA } from "@/lib/site-control-copy";

// The two role enum values the server route accepts (z.enum).
const ROLE_OPTIONS = [
  { value: "PRINCIPAL_CONTRACTOR", label: "Principal contractor" },
  { value: "SUBCONTRACTOR", label: "Subcontractor / trade" },
] as const;

type FieldName =
  | "email"
  | "company"
  | "fullName"
  | "activeSites"
  | "role"
  | "currentTool"
  | "timeline"
  | "consent";

type FieldErrors = Partial<Record<FieldName, string>>;

// Mirror of the route's z.string().email() — UX-only, server is truth.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SiteControlLeadFormProps {
  submitLabel?: string;
}

export function SiteControlLeadForm({
  submitLabel = SITE_CONTROL_CTA,
}: SiteControlLeadFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  // Review M10: Turnstile tokens are single-use — keep a ref to the widget so
  // every submit attempt that posted a token can reset it for the retry.
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const submissionIdRef = useRef<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const form = e.currentTarget;
    const formData = new FormData(form);

    const email = String(formData.get("email") ?? "").trim();
    const company = String(formData.get("company") ?? "").trim();
    const fullName = String(formData.get("fullName") ?? "").trim();
    const activeSitesRaw = String(formData.get("activeSites") ?? "").trim();
    const role = String(formData.get("role") ?? "");
    const currentTool = String(formData.get("currentTool") ?? "").trim();
    const timeline = String(formData.get("timeline") ?? "").trim();
    // Honeypot — humans never fill this; included verbatim in the POST body.
    const website = String(formData.get("website") ?? "");

    // Cloudflare injects `cf-turnstile-response` inside the form once the
    // widget solves; with no site key the widget renders a manual
    // `turnstileToken` input instead. Read both — an empty token is rejected
    // server-side regardless of environment.
    const turnstileToken = readTurnstileToken(form);

    // Client-side mirror of the server schema (UX-only).
    const localErrors: FieldErrors = {};
    if (!email || !EMAIL_REGEX.test(email)) {
      localErrors.email = "Enter a valid work email";
    }
    if (!company) localErrors.company = "Required";
    if (!fullName) localErrors.fullName = "Required";
    const activeSites = Number(activeSitesRaw);
    if (
      !activeSitesRaw ||
      !Number.isFinite(activeSites) ||
      !Number.isInteger(activeSites) ||
      activeSites < 1
    ) {
      localErrors.activeSites = "Enter the number of active sites (1 or more)";
    }
    if (role !== "PRINCIPAL_CONTRACTOR" && role !== "SUBCONTRACTOR") {
      localErrors.role = "Select your role";
    }
    if (!consent) {
      localErrors.consent = "Please accept the privacy notice to continue";
    }
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }

    // Review M10: when a site key is configured the widget MUST be solved
    // before we post — otherwise the server 400s with a Turnstile error that
    // maps to no field, leaving "check the highlighted fields" with nothing
    // highlighted. Without a key the widget renders the manual token input
    // instead, which the local suite fills — an empty token is never fine, the
    // server rejects it in every environment.
    if (resolveTurnstileSiteKey() && !turnstileToken) {
      toast.error("Please complete the verification check before submitting.");
      return;
    }

    // Build the POST body in the EXACT shape the Plan-03 Zod schema expects.
    // Optional fields are omitted when blank so they never become empty
    // strings the server has to special-case.
    submissionIdRef.current = getOrCreatePublicFormSubmissionId(
      submissionIdRef.current,
    );
    const payload: Record<string, unknown> = {
      email,
      company,
      fullName,
      activeSites, // number — matches z.coerce.number()
      role,
      consent: true, // only sent when the box is ticked (guarded above)
      turnstileToken,
      website, // honeypot — empty for humans
      // Review H6 (MKT-2): without this, the route's attribution passthrough
      // (step 9) always resolved to {} and every SiteControlLead landed with
      // empty UTM/gclid columns — unrecoverable for a paid-acquisition GTM.
      // Same pattern as the /demo lead capture; the schema .passthrough()es it.
      firstAttribution: readBrowserAttribution(),
      lastAttribution: readBrowserAttributionWithCurrentUrl(),
      // Backward-compatible alias while older deployments still read a single
      // attribution object. The server treats this as last/conversion touch.
      attribution: readBrowserAttributionWithCurrentUrl(),
      // This is the separate cookie choice that maps to Google's ad_user_data
      // consent. It is deliberately not inferred from the contact checkbox.
      adUserDataConsent: readAdUserDataConsent(),
    };
    if (submissionIdRef.current) {
      payload.submissionId = submissionIdRef.current;
    }
    if (currentTool) payload.currentTool = currentTool;
    if (timeline) payload.timeline = timeline;

    setSubmitting(true);
    try {
      const res = await fetch("/api/site-control/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        // Created and recent-duplicate submissions deliberately have the same
        // 202 response. Do not emit a client conversion that would either
        // reveal the branch or over-count duplicates; the server owns the
        // canonical PostHog and Google outbox events for genuinely new rows.
        setSubmitted(true);
        toast.success("Thanks — we'll be in touch to book your walkthrough.");
        return;
      }

      if (res.status === 429) {
        toast.error("Too many requests. Please wait a moment and try again.");
        setSubmitting(false);
        return;
      }

      // 400 (validation) / 409 (conflict) — try to map field errors.
      const data = (await res.json().catch(() => ({}))) as {
        error?: { fieldErrors?: Record<string, string[]> } | string;
      };
      // Review M10: the server's Turnstile rejection maps to no field — the
      // posted token was spent/expired. The `finally` reset below issues a
      // fresh one, so the user just needs to retry.
      if (
        res.status === 400 &&
        typeof data?.error === "string" &&
        data.error.toLowerCase().includes("turnstile")
      ) {
        toast.error("Verification expired — please try again.");
        setSubmitting(false);
        return;
      }
      if (
        res.status === 400 &&
        data?.error &&
        typeof data.error === "object" &&
        data.error.fieldErrors
      ) {
        const fe = data.error.fieldErrors;
        const mapped: FieldErrors = {};
        for (const k of Object.keys(fe) as FieldName[]) {
          const first = fe[k]?.[0];
          if (first) mapped[k] = first;
        }
        setErrors(mapped);
        toast.error("Please check the highlighted fields and try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      setSubmitting(false);
    } catch {
      toast.error("Couldn't reach the server. Please try again.");
      setSubmitting(false);
    } finally {
      // Review M10: the token we just posted is spent regardless of outcome —
      // reset the widget so a retry submits a fresh token instead of a
      // guaranteed "Turnstile verification failed" 400.
      resetTurnstile(turnstileRef.current);
    }
  };

  const inputBaseClass =
    "w-full rounded-lg border border-border px-3 py-3 text-base md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent min-h-[48px]";

  const fieldClass = (field: FieldName) =>
    errors[field]
      ? `${inputBaseClass} border-danger focus:ring-danger`
      : inputBaseClass;

  // Terminal success state — replaces the form so there's no double submit.
  if (submitted) {
    return (
      <div className="mx-auto w-full max-w-md rounded-2xl border border-done-line bg-done-bg p-8 text-center">
        <CheckCircle2
          className="mx-auto h-10 w-10 text-done"
          aria-hidden="true"
        />
        <h3 className="mt-4 font-heading text-xl font-bold text-navy">
          Request received
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          Thanks for your interest in {SITE_CONTROL_NAME}. We&apos;ll be in
          touch shortly to book your walkthrough.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="mx-auto w-full max-w-md space-y-5"
    >
      {/* Honeypot — visually hidden, off the tab order, ignored by AT.
          Humans never fill it; bots that fill every input trip the server. */}
      <div aria-hidden="true" className="sr-only">
        <label htmlFor="sc-website">Leave this field empty</label>
        <input
          id="sc-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>

      {/* Work email */}
      <div>
        <label
          htmlFor="sc-email"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Work email <span className="text-danger">*</span>
        </label>
        <input
          id="sc-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@yourcompany.co.uk"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "sc-email-error" : undefined}
          className={fieldClass("email")}
        />
        {errors.email ? (
          <p id="sc-email-error" className="mt-1 text-xs text-danger">
            {errors.email}
          </p>
        ) : null}
      </div>

      {/* Company */}
      <div>
        <label
          htmlFor="sc-company"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Company <span className="text-danger">*</span>
        </label>
        <input
          id="sc-company"
          name="company"
          type="text"
          required
          autoComplete="organization"
          placeholder="Your construction firm"
          aria-invalid={!!errors.company}
          aria-describedby={errors.company ? "sc-company-error" : undefined}
          className={fieldClass("company")}
        />
        {errors.company ? (
          <p id="sc-company-error" className="mt-1 text-xs text-danger">
            {errors.company}
          </p>
        ) : null}
      </div>

      {/* Full name */}
      <div>
        <label
          htmlFor="sc-fullName"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Full name <span className="text-danger">*</span>
        </label>
        <input
          id="sc-fullName"
          name="fullName"
          type="text"
          required
          autoComplete="name"
          placeholder="Your name"
          aria-invalid={!!errors.fullName}
          aria-describedby={errors.fullName ? "sc-fullName-error" : undefined}
          className={fieldClass("fullName")}
        />
        {errors.fullName ? (
          <p id="sc-fullName-error" className="mt-1 text-xs text-danger">
            {errors.fullName}
          </p>
        ) : null}
      </div>

      {/* Number of active sites */}
      <div>
        <label
          htmlFor="sc-activeSites"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Number of active sites <span className="text-danger">*</span>
        </label>
        <input
          id="sc-activeSites"
          name="activeSites"
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          required
          placeholder="e.g. 3"
          aria-invalid={!!errors.activeSites}
          aria-describedby={
            errors.activeSites ? "sc-activeSites-error" : undefined
          }
          className={fieldClass("activeSites")}
        />
        {errors.activeSites ? (
          <p id="sc-activeSites-error" className="mt-1 text-xs text-danger">
            {errors.activeSites}
          </p>
        ) : null}
      </div>

      {/* Role */}
      <div>
        <label
          htmlFor="sc-role"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Your role <span className="text-danger">*</span>
        </label>
        <select
          id="sc-role"
          name="role"
          required
          defaultValue=""
          aria-invalid={!!errors.role}
          aria-describedby={errors.role ? "sc-role-error" : undefined}
          className={fieldClass("role")}
        >
          <option value="" disabled>
            Select your role
          </option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        {errors.role ? (
          <p id="sc-role-error" className="mt-1 text-xs text-danger">
            {errors.role}
          </p>
        ) : null}
      </div>

      {/* Current tool (optional) */}
      <div>
        <label
          htmlFor="sc-currentTool"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Current tool{" "}
          <span className="text-text-tertiary font-normal">(optional)</span>
        </label>
        <input
          id="sc-currentTool"
          name="currentTool"
          type="text"
          maxLength={500}
          placeholder="What you use today (if anything)"
          className={inputBaseClass}
        />
      </div>

      {/* Timeline (optional) */}
      <div>
        <label
          htmlFor="sc-timeline"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Timeline{" "}
          <span className="text-text-tertiary font-normal">(optional)</span>
        </label>
        <input
          id="sc-timeline"
          name="timeline"
          type="text"
          maxLength={500}
          placeholder="When do you want to go live?"
          className={inputBaseClass}
        />
      </div>

      {/* Cloudflare Turnstile — anti-bot. Renders nothing without a site key. */}
      <TurnstileWidget className="mt-2" widgetRef={turnstileRef} />

      {/* GDPR consent — UNTICKED by default; submit is blocked until ticked. */}
      <div>
        <label
          htmlFor="sc-consent"
          className="flex items-start gap-3 text-sm text-muted"
        >
          <input
            id="sc-consent"
            name="consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              if (e.target.checked) {
                setErrors((prev) => ({ ...prev, consent: undefined }));
              }
            }}
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? "sc-consent-error" : undefined}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-border text-accent focus:ring-2 focus:ring-accent"
          />
          <span>
            I agree to be contacted about {SITE_CONTROL_NAME} and have read the{" "}
            <Link
              href="/legal/privacy"
              className="font-medium text-brand-700 underline hover:text-accent-dark"
            >
              privacy notice
            </Link>
            .
          </span>
        </label>
        {errors.consent ? (
          <p id="sc-consent-error" className="mt-1 text-xs text-danger">
            {errors.consent}
          </p>
        ) : null}
      </div>

      {/* Submit — disabled until consent is ticked (LAND-06) or while in-flight. */}
      <button
        type="submit"
        disabled={!consent || submitting}
        className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Sending…
          </>
        ) : (
          submitLabel
        )}
      </button>
    </form>
  );
}
